import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import StaffLivePreview from "./StaffLivePreview";
import StaffWarningPicker from "./StaffWarningPicker";
import {HOST_WARNINGS} from "./staffLiveWarnings";

const API = "/api/staff/lives";

async function request(path, options = {}) {
  const token = localStorage.getItem("evoAccessToken") || "";
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {})
    }
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || data.message || "Action impossible.");
  }
  return data;
}

export default function StaffLivesActivePage() {
  const navigate = useNavigate();
  const [lives, setLives] = useState([]);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [error, setError] = useState("");
  const [warningLive, setWarningLive] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await request("/active");
      setLives(Array.isArray(data.lives) ? data.lives : []);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [refresh]);

  const openPreview = async live => {
    if (previewId === live.id) {
      setPreviewId(null);
      return;
    }
    if (live.provider !== "mediasoup" || !live.room_name) {
      setError("Flux vidéo de ce live indisponible.");
      return;
    }
    setBusyId(live.id);
    setError("");
    try {
      await request(`/${live.id}/view`, {method: "POST"});
      setPreviewId(live.id);
    } catch (err) {
      setError(err.message || "Impossible d’ouvrir le live.");
    } finally {
      setBusyId(null);
    }
  };

  const moderate = async (live, action, selectedReason) => {
    const reason = String(selectedReason || reasons[live.id] || "").trim();
    if (!reason) {
      setError("Indique un motif avant d’avertir ou de couper le live.");
      return;
    }
    if (action === "stop" && !window.confirm(
      `Couper maintenant le live « ${live.title || live.id} » ?`
    )) return;

    setBusyId(live.id);
    setError("");
    try {
      await request(`/${live.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({reason})
      });
      await refresh();
      setReasons(previous => ({...previous, [live.id]: ""}));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main style={{
      minHeight: "100vh", background: "#020912", color: "#fff",
      padding: "24px 16px 60px"
    }}>
      <div style={{maxWidth: 950, margin: "0 auto"}}>
        <button type="button" onClick={() => navigate("/staff")}
          style={{
            background: "none", border: "1px solid #00c8ee",
            borderRadius: 10, color: "#00eaff", padding: 10,
            cursor: "pointer"
          }}>
          ← Centre Staff
        </button>
        <h1 style={{marginBottom: 4}}>📡 Lives actifs</h1>
        <p style={{color: "#9bb8c8"}}>
          {lives.length} live(s) en cours · actualisation toutes les 10 secondes
        </p>
        {error && <p role="alert" style={{color: "#ff718e"}}>{error}</p>}
        {loading ? <p>Chargement…</p> :
          lives.length === 0 ? <p>Aucun live actif actuellement.</p> :
            lives.map(live => (
              <article key={live.id} style={{
                border: "1px solid #175269", borderRadius: 14,
                background: "#091d2d", padding: 18, marginBottom: 14
              }}>
                <h2 style={{color: "#00eaff", marginTop: 0}}>
                  {live.title || "Live sans titre"} · #{live.id}
                </h2>
                <div>Créateur : {live.pseudo || "Membre EVO"}</div>
                <div style={{color: "#9bb8c8", marginTop: 8}}>
                  👥 {Number(live.viewer_count || 0)} spectateur(s)
                  {live.duration_minutes != null &&
                    ` · ${live.duration_minutes} min`}
                </div>
                <button type="button" disabled={busyId !== null}
                  onClick={() => openPreview(live)}
                  style={{
                    display: "block", marginTop: 14, padding: "10px 15px",
                    borderRadius: 9, border: "1px solid #00eaff",
                    background: "#07394b", color: "#fff", cursor: "pointer"
                  }}>
                  {previewId === live.id ? "Fermer la vidéo" : "👀 Voir le live"}
                </button>
                {previewId === live.id && (
                  <StaffLivePreview liveId={live.id}
                    roomName={live.room_name}
                    viewerCount={Number(live.viewer_count || 0)}
                  />
                )}
                <select
                  aria-label={`Motif de coupure du live ${live.id}`}
                  value={reasons[live.id] || ""}
                  onChange={event => setReasons(previous => ({
                    ...previous, [live.id]: event.target.value
                  }))}
                  style={{
                    display: "block", width: "100%", marginTop: 15,
                    padding: 12, borderRadius: 9,
                    border: "1px solid #28607a",
                    background: "#04111c", color: "#fff"
                  }}>
                  <option value="">Choisir un motif si le live doit être coupé</option>
                  {HOST_WARNINGS.map(item => (
                    <option key={item.label} value={item.message}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <div style={{display: "flex", gap: 10, marginTop: 12}}>
                  <button type="button" disabled={busyId !== null}
                    onClick={() => setWarningLive(live)}
                    style={{
                      background: "#21301f", color: "#ffcf6a",
                      border: "1px solid #ffcf6a", borderRadius: 9,
                      padding: "10px 15px", cursor: "pointer"
                    }}>
                    Avertir
                  </button>
                  <button type="button" disabled={busyId !== null}
                    onClick={() => moderate(live, "stop")}
                    style={{
                      background: "#321825", color: "#ff718e",
                      border: "1px solid #ff718e", borderRadius: 9,
                      padding: "10px 15px", cursor: "pointer"
                    }}>
                    Couper le live
                  </button>
                </div>
              </article>
            ))}
      </div>
      {warningLive && (
        <StaffWarningPicker
          title={`Avertir le créateur du live #${warningLive.id}`}
          warnings={HOST_WARNINGS}
          onClose={() => setWarningLive(null)}
          onSelect={reason => {
            const live = warningLive;
            setWarningLive(null);
            moderate(live, "warn", reason);
          }}
        />
      )}
    </main>
  );
}
